import React, { useMemo } from "react";
import {
  OpusAgentArticleSource,
  OpusAgentLibraryItemSource,
} from "./OpusAgentSourceCard";
import { Skeleton } from "@src/lib/ui/Skeleton";
import { useChatReferences } from "@src/features/aiAssist/hooks/useChatReferences";
import type { OpusAgentTurnFragment } from "./useOpusAgentChat.generated";

export type OpusAgentCitation = OpusAgentTurnFragment["citations"][number];

// file_citation cards key off the library item id; intercom_article cards
// key off the url. Citations without a key are skipped, mirroring
// MessageAnnotations.
const getCitationLookupId = (citation: OpusAgentCitation): string => {
  if (citation.type === "file_citation") return citation.libraryItemId ?? "";
  return citation.url ?? "";
};

interface OpusAgentCitationsProps {
  citations: OpusAgentCitation[];
}

// Source cards for an answer's citations, de-duplicated per item. Unlike
// MessageAnnotations there is no [n] number or reference count: the agent's
// reply text carries no [n] markers for them to point at.
export const OpusAgentCitations: React.FC<OpusAgentCitationsProps> = ({
  citations,
}) => {
  const supportedCitations = useMemo(
    () =>
      citations.filter(
        (citation) =>
          citation.type === "file_citation" ||
          citation.type === "intercom_article",
      ),
    [citations],
  );

  const uniqueCitations = useMemo(() => {
    const uniqueMap = new Map<string, OpusAgentCitation>();
    supportedCitations.forEach((citation) => {
      const key = getCitationLookupId(citation);
      if (key && !uniqueMap.has(key)) uniqueMap.set(key, citation);
    });
    return [...uniqueMap.values()];
  }, [supportedCitations]);

  const libraryItemIds = useMemo(
    () =>
      Array.from(
        new Set(
          uniqueCitations
            .filter((citation) => citation.type === "file_citation")
            .map((citation) => citation.libraryItemId)
            .filter((id): id is string => !!id),
        ),
      ),
    [uniqueCitations],
  );

  const { references, loading: referencesLoading } =
    useChatReferences(libraryItemIds);

  const libraryItemsByLookupId = useMemo(
    () => new Map(references.map((ref) => [ref.lookupId, ref.libraryItem])),
    [references],
  );

  if (uniqueCitations.length === 0) return null;

  return (
    <div
      className="mt-2 flex flex-col gap-1.5"
      data-testid="opus-agent-sources"
    >
      {referencesLoading
        ? uniqueCitations.map((citation, idx) => (
            <div
              key={getCitationLookupId(citation) || idx}
              aria-hidden
              data-testid="opus-agent-source-skeleton"
              className="flex items-center gap-3 rounded-2xl bg-bg-secondary p-3"
            >
              <Skeleton className="size-11 rounded-xl" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3.5 w-40 rounded" />
                <Skeleton className="h-3 w-20 rounded" />
              </div>
            </div>
          ))
        : uniqueCitations.map((citation) => {
            const lookupId = getCitationLookupId(citation);
            return citation.type === "file_citation" ? (
              <OpusAgentLibraryItemSource
                key={`opus-agent-citation-${lookupId}`}
                libraryItem={libraryItemsByLookupId.get(lookupId)}
                fallbackTitle={citation.title ?? ""}
              />
            ) : (
              <OpusAgentArticleSource
                key={`opus-agent-citation-${lookupId}`}
                url={citation.url ?? ""}
                title={citation.title ?? null}
              />
            );
          })}
    </div>
  );
};
